import type { PageTemplateAssignment } from '../types';

/**
 * Page selection for re-running part of an existing OCR run (OMA-674).
 *
 * Re-assigning a template used to send EVERY page through a fresh start, which
 * archives the whole run; the operator now ticks the pages to re-read and only
 * those are sent to `rerun-pages`. These are the rules behind the row
 * checkboxes and the tri-state "select all", kept pure so they are pinned by
 * `tests/unit/ocr-page-selection.test.ts`.
 */

/** The header checkbox: all, none, or some of `pageIds` selected. */
export function selectAllState(
  selected: ReadonlySet<number>,
  pageIds: readonly number[]
): boolean | 'indeterminate' {
  // Counted against the CURRENT page list: an id left over from before a
  // refetch must not make a partial selection read as complete.
  const count = pageIds.filter((id) => selected.has(id)).length;
  if (count === 0) return false;
  return count === pageIds.length ? true : 'indeterminate';
}

export function toggleAllPages(
  pageIds: readonly number[],
  checked: boolean
): Set<number> {
  return checked ? new Set(pageIds) : new Set();
}

export function togglePage(
  selected: ReadonlySet<number>,
  pageId: number,
  checked: boolean
): Set<number> {
  const next = new Set(selected);
  if (checked) next.add(pageId);
  else next.delete(pageId);
  return next;
}

/**
 * The `rerun-pages` payload: the selected pages in document order with their
 * templates. Null when nothing is selected or a selected page has no template
 * yet — the run button stays disabled rather than sending a partial request.
 */
export function selectedAssignments(
  pageIds: readonly number[],
  selected: ReadonlySet<number>,
  templateFor: (pageId: number) => number | null
): PageTemplateAssignment[] | null {
  const assignments: PageTemplateAssignment[] = [];

  for (const pageId of pageIds) {
    if (!selected.has(pageId)) continue;
    const templateId = templateFor(pageId);
    if (templateId == null) return null;
    assignments.push({ page_id: pageId, ocr_template_id: templateId });
  }

  return assignments.length > 0 ? assignments : null;
}
